import { Fragment } from "react";
import { Link, useLocation } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";
import { ChevronDown, ChevronUp, CircleHelp, LogOut, Moon } from "lucide-react";
import {
  ACCOUNT_MENU_DARK_MODE_COPY,
  ACCOUNT_MENU_LOG_OUT_COPY,
  IN_APP_HELP_STEP,
  WALKTHROUGH_NAME_COPY,
} from "@fomofi/shared";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ACCOUNT_LINKS, navItemFor } from "@/config/redesignNavItems";
import { useAccountTheme } from "@/hooks/use-account-theme";
import { reportSignOutFailure } from "@/lib/signOutFailure";
import { openWalkthrough } from "@/lib/walkthroughBridge";
import { cn } from "@/lib/utils";

/**
 * The account menu (redesign spec, "The new navigation"): Settings & profile,
 * Connected banks, Subscription, Help & support, Dark mode, Log out. Everything about
 * the account lives here and nowhere in the page nav.
 *
 * Two triggers, one menu. From lg it sits at the foot of the sidebar, as in the
 * mockup; below lg the sidebar is gone, so the same menu opens from the avatar in the
 * page header (ProfileMenu). Dark mode writes the account's one theme setting
 * (AN-52), the same control Profile's Appearance and the Home header use.
 */
function useAccountIdentity() {
  const { user } = useUser();
  // A real name or a neutral label, never the email's local part: it carries
  // plus-tags and reads as the system leaking (BEN-141 4b item 6, DashboardHeader).
  // The email itself shows in the menu's label, where it is labelled as one.
  const displayName = user?.fullName?.trim() || user?.firstName?.trim() || "Your account";
  const email = user?.primaryEmailAddress?.emailAddress ?? "";
  const initials =
    [user?.firstName?.[0], user?.lastName?.[0]].filter(Boolean).join("").toUpperCase() ||
    user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() ||
    "U";
  return { displayName, email, initials, avatarUrl: user?.imageUrl };
}

function Avatar({
  initials,
  avatarUrl,
  className,
}: {
  initials: string;
  avatarUrl?: string;
  className?: string;
}) {
  return avatarUrl ? (
    <img src={avatarUrl} alt="" className={cn("shrink-0 rounded-full", className)} />
  ) : (
    <span
      aria-hidden="true"
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-[11px] font-bold text-sidebar-foreground",
        className,
      )}
    >
      {initials}
    </span>
  );
}

/**
 * Doc 3 F11, A6: the walkthrough's permanent home, Help & support → How Fomo-Fi works,
 * at step 1. In the same menu on every width: the sidebar's from lg, the header avatar's
 * below it (the phone's tab bar has no menu of its own since I1).
 */
function HowFomoFiWorksItem() {
  // "Back to Plan": the nav item the page belongs to; an account page has none, and the
  // walkthrough then says "this page".
  const from = navItemFor(useLocation().pathname)?.label;
  return (
    <DropdownMenuItem
      // H5: the walkthrough must record the menu's trigger as its opener, so focus goes
      // back there when it closes. The menu hands focus back to its trigger only after it
      // unmounts, later than the walkthrough opens (flow pass: focus fell to the page), so
      // the trigger (the menu's own label, either one of the two) is focused first.
      onSelect={(event) => {
        const menu = (event.currentTarget as HTMLElement).closest('[role="menu"]');
        const triggerId = menu?.getAttribute("aria-labelledby");
        const trigger = triggerId ? document.getElementById(triggerId) : null;
        requestAnimationFrame(() => {
          trigger?.focus();
          openWalkthrough(IN_APP_HELP_STEP.accountMenu, from);
        });
      }}
      className="pl-7"
    >
      <CircleHelp className="h-4 w-4" aria-hidden="true" />
      {WALKTHROUGH_NAME_COPY}
    </DropdownMenuItem>
  );
}

export function AccountMenuItems() {
  const { displayName, email } = useAccountIdentity();
  const { signOut } = useClerk();
  const { theme, setAccountTheme } = useAccountTheme();
  return (
    <>
      <DropdownMenuLabel className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">{displayName}</span>
        {email && (
          <span className="truncate text-xs font-normal text-muted-foreground">{email}</span>
        )}
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {ACCOUNT_LINKS.map((link) => (
        <Fragment key={link.to}>
          <DropdownMenuItem asChild>
            <Link to={link.to}>
              <link.icon className="h-4 w-4" aria-hidden="true" />
              {link.label}
            </Link>
          </DropdownMenuItem>
          {link.to === "/help" && <HowFomoFiWorksItem />}
        </Fragment>
      ))}
      <DropdownMenuCheckboxItem
        checked={theme === "dark"}
        onCheckedChange={(on) => setAccountTheme(on ? "dark" : "light")}
        // Keep the menu open so the switch visibly lands.
        onSelect={(event) => event.preventDefault()}
      >
        <Moon className="h-4 w-4" aria-hidden="true" />
        {ACCOUNT_MENU_DARK_MODE_COPY}
      </DropdownMenuCheckboxItem>
      <DropdownMenuSeparator />
      {/* FP-174: offline, sign-out fails instead of pretending. Say so. */}
      <DropdownMenuItem onClick={() => void signOut().catch(reportSignOutFailure)}>
        <LogOut className="h-4 w-4" aria-hidden="true" />
        {ACCOUNT_MENU_LOG_OUT_COPY}
      </DropdownMenuItem>
    </>
  );
}

/** The sidebar's foot: avatar, name and a quiet line of what is inside. */
export function SidebarAccountMenu({ collapsed }: { collapsed: boolean }) {
  const { displayName, initials, avatarUrl } = useAccountIdentity();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={collapsed ? `Account menu for ${displayName}` : undefined}
          className={cn(
            "flex w-full items-center gap-3 rounded-xl border border-sidebar-border text-left transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
            collapsed ? "justify-center p-2" : "px-3 py-2.5",
          )}
        >
          <Avatar initials={initials} avatarUrl={avatarUrl} className="h-8 w-8" />
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-sidebar-foreground">
                  {displayName}
                </span>
                <span className="block truncate text-xs text-sidebar-foreground/60">
                  Settings · Banks · Help
                </span>
              </span>
              <ChevronUp
                className="h-4 w-4 shrink-0 text-sidebar-foreground/60"
                aria-hidden="true"
              />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-60" sideOffset={8}>
        <AccountMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Below lg, the same menu from the page header's avatar. */
export function HeaderAccountMenu() {
  const { displayName, initials, avatarUrl } = useAccountIdentity();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open account menu"
          className="flex h-9 items-center gap-2 rounded-lg border border-border bg-background pl-1.5 pr-2 text-muted-foreground transition hover:text-foreground"
        >
          <Avatar
            initials={initials}
            avatarUrl={avatarUrl}
            className="h-7 w-7 bg-primary-fill text-primary-fill-foreground"
          />
          <span className="hidden max-w-[140px] truncate text-[13px] font-medium text-foreground sm:inline">
            {displayName}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-60" sideOffset={8}>
        <AccountMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
